import { type ReactNode, useEffect, useState } from "react";
import type SwiperClass from "swiper";
import { Keyboard, Mousewheel } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/scss";

import { HaNSliderCard } from "./components/HaNSliderCard";
import { HaNSliderCardButton } from "./components/HaNSliderCardButton";

type HaNSliderProps = {
  focusedIndex: number;
  onFocusChange: (index: number) => void;
  children: ReactNode;
};

type HaNSliderCompound = typeof HaNSliderRoot & {
  // Swiper only finds its slides among its children and fragments, so slides are Swiper's own
  Slide: typeof SwiperSlide;
  Card: typeof HaNSliderCard;
  CardButton: typeof HaNSliderCardButton;
};

const HaNSliderRoot = ({ focusedIndex, onFocusChange, children }: HaNSliderProps) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  // Swiper keeps its own position, so it's moved along when the focus changes elsewhere, like a tap on the map. Moving
  // it skips the slide callbacks, so this doesn't report a focus change back.
  useEffect(
    function syncCarouselToFocusedItem() {
      if (!swiper || swiper.destroyed || focusedIndex < 0 || swiper.activeIndex === focusedIndex) return;

      swiper.slideTo(focusedIndex, undefined, false);
    },
    [focusedIndex, swiper],
  );

  return (
    <Swiper
      className="w-full [&_.swiper-slide]:h-auto [&_.swiper-slide]:w-auto"
      initialSlide={Math.max(focusedIndex, 0)}
      centeredSlides
      slidesPerView="auto"
      spaceBetween={16}
      grabCursor
      keyboard
      mousewheel={{ forceToAxis: true }}
      modules={[Keyboard, Mousewheel]}
      onSwiper={setSwiper}
      onSlideChange={current => onFocusChange(current.activeIndex)}>
      {children}
    </Swiper>
  );
};

/** Swipes through a tab's results on phones, one centred at a time, and says which one the user swiped to. */
export const HaNSlider: HaNSliderCompound = Object.assign(HaNSliderRoot, {
  Slide: SwiperSlide,
  Card: HaNSliderCard,
  CardButton: HaNSliderCardButton,
});
